import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  subscribeToTerraFlowProjects,
  getTerraFlowProjects,
  syncTerraFlowProjects,
} from '../utils/storage';
import LoadingScreen from './LoadingScreen';
import { EmptyState } from './templates';
import './EquipmentPage.css';   // reuse the equipment header / refresh styling
import './TerraFlowManager.css';

export default function TerraFlowManager() {
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [pageReady, setPageReady] = useState(false);
  const [syncing,  setSyncing]  = useState(false);
  const [syncNote, setSyncNote] = useState(null);

  useEffect(() => {
    const unsub = subscribeToTerraFlowProjects((list) => {
      setProjects(list);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setSyncNote(null);
    try {
      const res = await syncTerraFlowProjects();
      // One-shot refresh so the table reflects the backfill immediately.
      setProjects(await getTerraFlowProjects());
      setSyncNote(
        res?.configured
          ? `Synced ${res.synced ?? 0} project(s) from TerraFlow.`
          : 'TerraFlow is not configured yet — nothing to pull.'
      );
    } catch (err) {
      setSyncNote(err?.message || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  }, []);

  if (!pageReady) {
    return <LoadingScreen isLoading={loading} onDone={() => setPageReady(true)} />;
  }

  const filtered = projects;

  return (
    <div className="equipment-page">
      <div className="equipment-page-header">
        <div className="equipment-title-row">
          <h1 className="equipment-page-title">TerraFlow Projects</h1>
        </div>
        <button className="btn btn-secondary equipment-refresh" onClick={handleSync} disabled={syncing}>
          {syncing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {syncNote && (
        <div className="tf-sync-note">{syncNote}</div>
      )}

      {filtered.length === 0 ? (
        <EmptyState icon="🔗">
          <h2>No TerraFlow projects yet</h2>
          <p>Projects appear here once TerraFlow syncs them in. Use “Refresh” to pull the latest.</p>
        </EmptyState>
      ) : (
        <div className="table-wrapper">
          <table className="ds-table ds-table-fixed tf-table">
            <colgroup>
              <col className="tf-col-name" />
              <col className="tf-col-linked" />
              <col className="tf-col-chevron" />
            </colgroup>
            <thead>
              <tr>
                <th>Project ({filtered.length})</th>
                <th>Linked Objects</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="tf-row"
                  onClick={() => navigate(`/terraflow/project/${p.id}`)}
                >
                  <td className="tf-name">
                    {!(Array.isArray(p.objectIds) && p.objectIds.length > 0) && (
                      <span className="tf-pending-dot" title="No TIB object assigned" />
                    )}
                    {Array.isArray(p.pendingChanges) && p.pendingChanges.length > 0 && (
                      <span className="tf-changed-dot" title="Updated by TerraFlow — open to review" />
                    )}
                    {p.name || '—'}
                  </td>
                  <td className="tf-linked">
                    {Array.isArray(p.objectIds) && p.objectIds.length > 0
                      ? `${p.objectIds.length} linked`
                      : <span className="tf-unlinked">Not linked</span>}
                  </td>
                  <td className="tf-chevron-cell">›</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
